/**
 * Shared sessionStorage stamp for stale-deploy recovery reloads.
 *
 * Both the `vite:preloadError` handler (src/api/clientErrors.ts) and the
 * `React.lazy` rejection handler (src/routes/lazyNamed.ts) use the same
 * sessionStorage key and window so that a recovery reload triggered by
 * either path prevents the other from reloading again within the window.
 */

/** sessionStorage key holding the timestamp (ms) of the last recovery reload. */
export const PRELOAD_RELOAD_AT_KEY = 'vite-preload-reloaded-at';

/**
 * How long (ms) after a recovery reload before we consider a new chunk failure
 * to be a separate episode rather than a persistent breakage. Within this
 * window, a repeated failure is treated as genuine (report, don't reload).
 */
export const PRELOAD_RELOAD_WINDOW_MS = 10_000;

/** Read the last recovery-reload timestamp from sessionStorage. Returns 0 if none. */
export function readPreloadReloadAt(): number {
  try {
    const raw = sessionStorage.getItem(PRELOAD_RELOAD_AT_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    // sessionStorage can throw in privacy modes — treat as "never reloaded".
    return 0;
  }
}

/** Write the recovery-reload timestamp into sessionStorage. */
export function writePreloadReloadAt(timestamp: number): void {
  try {
    sessionStorage.setItem(PRELOAD_RELOAD_AT_KEY, String(timestamp));
  } catch {
    // No sessionStorage — recovery reload will still run; the guard just
    // won't remember it, which is acceptable (worst case: one extra reload).
  }
}
